import React, { useState, useEffect, useRef } from "react";
import "./CrossfadeImageColumns.css";

const CrossfadeImageColumns = ({
  images,
  columns = 2,
  interval = 3000,
  paddingLeft = 0,
  paddingRight = 0,
  gap = 10,
  useDynamicPadding = false,
  maxPadding = 300, // Maximum padding for large screens
  disablePaddingOnMobile = false, // Disable padding on mobile screens
}) => {
  const [currentIndices, setCurrentIndices] = useState(Array(columns).fill(0));
  const [prevIndices, setPrevIndices] = useState(Array(columns).fill(null));
  const [containerHeight, setContainerHeight] = useState("auto");
  const [calculatedPadding, setCalculatedPadding] = useState({
    paddingLeft,
    paddingRight,
  });
  const containerRef = useRef(null);

  const updateHeights = () => {
    if (containerRef.current) {
      const activeImages = containerRef.current.querySelectorAll(
        ".crossfade-image.fade-in"
      );

      if (activeImages.length > 0) {
        let maxHeight = 0;

        activeImages.forEach((img) => {
          const renderedHeight = img.getBoundingClientRect().height;
          maxHeight = Math.max(maxHeight, renderedHeight || img.clientHeight);
        });

        setContainerHeight(maxHeight > 0 ? maxHeight : "auto");
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (useDynamicPadding) {
        const screenWidth = window.innerWidth;

        if (disablePaddingOnMobile && screenWidth <= 768) {
          setCalculatedPadding({ paddingLeft: 0, paddingRight: 0 });
        } else if (screenWidth <= 1024) {
          const scaledPadding = (screenWidth / 1024) * (maxPadding / 2);
          setCalculatedPadding({
            paddingLeft: scaledPadding,
            paddingRight: scaledPadding,
          });
        } else if (screenWidth <= 1920) {
          const scaledPadding = (screenWidth / 1920) * maxPadding;
          setCalculatedPadding({
            paddingLeft: scaledPadding,
            paddingRight: scaledPadding,
          });
        } else {
          setCalculatedPadding({
            paddingLeft: maxPadding,
            paddingRight: maxPadding,
          });
        }
      }
    };

    handleResize(); // Set initial padding
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [useDynamicPadding, maxPadding, disablePaddingOnMobile]);

  useEffect(() => {
    const intervalIds = images.map((_, colIndex) =>
      setInterval(() => {
        setCurrentIndices((prevIndices) => {
          const newIndices = [...prevIndices];
          const nextIndex =
            (prevIndices[colIndex] + 1) % images[colIndex].length;

          setPrevIndices((prev) => {
            const updatedPrev = [...prev];
            updatedPrev[colIndex] = prevIndices[colIndex];
            return updatedPrev;
          });

          newIndices[colIndex] = nextIndex;
          return newIndices;
        });
      }, interval)
    );

    return () => intervalIds.forEach((id) => clearInterval(id));
  }, [columns, interval, images]);

  useEffect(() => {
    updateHeights();
    const handleResize = () => updateHeights();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentIndices]);

  return (
    <div
      className="crossfade-image-columns"
      ref={containerRef}
      style={{
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        paddingLeft: `${calculatedPadding.paddingLeft}px`,
        paddingRight: `${calculatedPadding.paddingRight}px`,
        gap: `${gap}px`,
        width: "100%",
        height: containerHeight,
      }}
    >
      {images.map((colImages, colIndex) => (
        <div key={colIndex} className="column">
          {colImages.map((image, imgIndex) => (
            <img
              key={imgIndex}
              src={image.src}
              alt={image.alt}
              className={`crossfade-image ${
                imgIndex === currentIndices[colIndex]
                  ? "fade-in"
                  : imgIndex === prevIndices[colIndex]
                  ? "fade-out"
                  : ""
              }`}
              onLoad={updateHeights}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default CrossfadeImageColumns;
